/**
 * Экран режима «Основы».
 *
 * Новичку показывается ровно то, что ему нужно знать сейчас: какие клавиши
 * уже открыты, какую группу он осваивает и каким пальцем её нажимают.
 * Непрозрачный тренажёр не объясняет, почему становится легче (ТЗ §6, режим 2).
 *
 * Над текстом стоит только осваиваемая группа: остальное — какой это ряд,
 * какие клавиши уже открыты, чем короткие сочетания отличаются от слов —
 * раскрывается под текстом (ТЗ FR-EN-84).
 */

import type { Translate } from '@type2x/i18n';
import type { TypingSession } from '@type2x/typing-engine';
import { Card } from '@type2x/ui';
import { keyGroups, type FundamentalsPlan } from '@type2x/feature-fundamentals';

import type { SessionId } from '@type2x/shared-contracts';

import type { Runtime } from '../runtime.js';
import { DrillLayout } from './DrillLayout.js';
import { TypingScreen } from './TypingScreen.js';

export interface FundamentalsScreenProps {
  readonly runtime: Runtime;
  readonly t: Translate;
  readonly plan: FundamentalsPlan;
  readonly sessionId: SessionId;
  readonly onFinished: (run: TypingSession) => void;
}

export function FundamentalsScreen({
  runtime,
  t,
  plan,
  sessionId,
  onFinished,
}: FundamentalsScreenProps): React.JSX.Element {
  const { group } = plan;
  if (group === null) return <Card title={t('fundamentals.title')}>{t('fundamentals.done')}</Card>;

  const groups = keyGroups({
    language: plan.language,
    layoutId: plan.layoutId,
    units: {},
    stats: {},
  });
  const totalKeys = groups.reduce((count, item) => count + item.characters.length, 0);

  return (
    <DrillLayout
      t={t}
      title={t('fundamentals.title')}
      now={
        <>
          {/* Осваиваемая группа и список открытых клавиш — разные вещи, и
              выглядеть должны по-разному. Раньше строка из всех открытых
              стояла под заголовком крупно и без подписи, и читалась как
              задание: «мне что, все восемь букв набирать?» */}
          <span className="t2x-muted">{t('fundamentals.current')}</span>{' '}
          <span className="t2x-keys">{group.characters.join(' ')}</span>
        </>
      }
      progress={{
        value: groups.length === 0 ? 0 : plan.completedGroupIds.length / groups.length,
        label: t('fundamentals.progress'),
      }}
      details={
        <>
          <p className="t2x-muted">
            {t('fundamentals.group', {
              row: t(`fundamentals.row.${group.row}`),
              finger: t(`fundamentals.finger.${group.fingerPair}`),
            })}
          </p>

          <p className="t2x-muted">
            {t('fundamentals.unlockedCount', {
              open: plan.unlockedCharacters.length,
              total: totalKeys,
              keys: plan.unlockedCharacters.join(' '),
            })}
          </p>

          <p className="t2x-muted">
            {t(plan.exercise.usesWords ? 'fundamentals.words' : 'fundamentals.chunks')}
          </p>

          <p className="t2x-muted">{t('fundamentals.hint')}</p>
        </>
      }
    >
      <TypingScreen
        key={sessionId}
        runtime={runtime}
        t={t}
        title={t('fundamentals.typing')}
        hint={t('fundamentals.hint')}
        sessionId={sessionId}
        layoutId={plan.layoutId}
        targetText={plan.exercise.text}
        hintLevel={plan.hintLevel}
        compact
        onFinished={onFinished}
      />
    </DrillLayout>
  );
}
